import {
  IconBell,
  IconBellGray,
  IconBinocularsGray,
  IconChainLink,
  IconChainLinkGray,
  IconChartBarGray,
  IconChartRadius,
  IconClockGray,
  IconCloudCheckGray,
  IconDashboard,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconSecurity,
  IconShareGray,
  IconTagGray,
  IconTvocGray,
  IconUserCheck,
  IconWarning,
  IconWarningGray,
  IconZoneGray,
} from '@assets/images/icon';
import { IconTemp } from '@assets/images/main';
import { rtlsSystem } from '@assets/images/solution';
import video from '@src/assets/images/solution/constructionSafety/video/rtlsVideo.mp4';

import image1 from '@src/assets/images/solution/cityTraffic/img/1.avif';
import image2 from '@src/assets/images/solution/cityTraffic/img/2.avif';
import image3 from '@src/assets/images/solution/cityTraffic/img/3.avif';
import image4 from '@src/assets/images/solution/cityTraffic/img/4.avif';
import image5 from '@src/assets/images/solution/cityTraffic/img/5.avif';
import image6 from '@src/assets/images/solution/cityTraffic/img/6.avif';
import image7 from '@src/assets/images/solution/cityTraffic/img/7.avif';
import image8 from '@src/assets/images/solution/cityTraffic/img/8.avif';
import image9 from '@src/assets/images/solution/cityTraffic/img/9.avif';

export default {
  type: 'Traffic Solution',

  banner: {
    title: 'AI-Based Vehicle Analysis for Smart Highway Rest Area Parking Management',
    desc: 'With PTZ AI cameras, vehicle positions and license plates in the parking area are recognized in real time, reducing congestion, improving operational efficiency, and enhancing the customer experience.',
  },

  video: {
    title: 'Optimize Operations and Convenience with AI-Based Parking Analysis',
    desc: 'AI cameras recognize vehicle locations and license plates in real time, display parking availability instantly, and link with user information for smart operations.',
    play: video,
  },

  func: [
    {
      theme: 'AI-Based Vehicle Tracking and Environmental Adaptation',
      title: 'Wide-Area Precision Monitoring with PTZ Cameras',
      desc: 'Multiple PTZ cameras installed on lighting towers cover vast parking areas seamlessly. High-zoom monitoring enables precise surveillance of critical zones, tracking parking flow and congested areas in real time.',
      sub1: 'Real-Time Status Monitoring',
      icon1: IconClockGray,
      sub2: 'Fast Data Integration',
      icon2: IconShareGray,
      image: image1,
    },
    {
      title: 'Field Data-Driven AI Model Enhancement',
      desc: 'ORBRO quickly trains object detection models to match parking lot conditions using real-time data augmentation, dramatically improving recognition accuracy shortly after installation.',
      sub1: 'Provides Statistical Data',
      icon1: IconChartBarGray,
      sub2: 'High Accuracy',
      icon2: IconRulerGray,
      image: image2,
    },
    {
      title: 'Stable Recognition in Nighttime and Low-Light Environments',
      desc: 'Even in darkness or low-light conditions, fast-moving vehicles are accurately captured using optimized low-light algorithms and NIR hardware support.',
      sub1: 'Dangerous Asset Designation',
      icon1: IconWarningGray,
      sub2: 'Configurable Response',
      icon2: IconBellGray,
      image: image3,
    },
    {
      title: 'Robust Performance in Harsh Weather Conditions',
      desc: 'Deep learning-based filtering algorithms adapt to weather changes, ensuring stable vehicle tracking performance during snow, rain, and other complex weather conditions.',
      sub1: 'Provides Statistical Data',
      icon1: IconChartBarGray,
      sub2: 'Cloud Data Storage',
      icon2: IconCloudCheckGray,
      image: image4,
    },
    {
      title: 'Simultaneous Indoor Traffic Analysis',
      desc: 'Adjacent indoor spaces are analyzed for customer flow using fisheye cameras, enhancing business utility by connecting parking and store traffic analysis.',
      sub1: 'Provides Statistical Data',
      icon1: IconChartBarGray,
      sub2: 'Cloud Data Storage',
      icon2: IconCloudCheckGray,
      image: image5,
    },
    {
      theme: 'Operational Efficiency and Automated Risk Detection',
      title: 'Vehicle Entry/Exit Traffic Analysis',
      desc: 'Automatically collects vehicle statistics at entry/exit points, predicts congestion by time slot, and provides data to optimize operating hours and facility improvements.',
      sub1: 'Provides Statistical Data',
      icon1: IconChartBarGray,
      sub2: 'Cloud Data Storage',
      icon2: IconCloudCheckGray,
      image: image6,
    },
    {
      title: 'Wrong-Way Driving Detection and Alert',
      desc: 'When a vehicle moves in the opposite direction of designated traffic, an immediate alert is triggered for quick response to dangerous situations.<br/><br/> • Detects objects moving contrary to designated directions in specific areas<br/><br/> • Detects wrong-way driving at rest area exits, etc.<br/><br/> • Provides alerts when wrong-way driving occurs (linked with mobile and control centers)',
      sub1: 'Provides Statistical Data',
      icon1: IconChartBarGray,
      sub2: 'Cloud Data Storage',
      icon2: IconCloudCheckGray,
      image: image7,
    },
    {
      title: 'Long-Term Parked Vehicle Analysis',
      desc: 'Detects pedestrians lingering for long periods in fast-moving entry/exit areas and sends alerts to prevent accidents. Also analyzes the risk of interaction between vehicles and pedestrians.',
      sub1: 'Provides Statistical Data',
      icon1: IconChartBarGray,
      sub2: 'Cloud Data Storage',
      icon2: IconCloudCheckGray,
      image: image8,
    },
    {
      title: 'Performance Optimization and Privacy Protection with Video ROI',
      desc: 'Unnecessary footage can be removed by setting ROI (region of interest), leaving only necessary views. This improves processing speed and anonymizes sensitive areas to protect privacy.',
      sub1: 'Provides Statistical Data',
      icon1: IconChartBarGray,
      sub2: 'Cloud Data Storage',
      icon2: IconCloudCheckGray,
      image: image9,
    },
  ],

  subfunc: {
    title: 'Various Features',
    desc: 'Managing medical waste requires constant vigilance. Even a small oversight or mistake can lead to serious medical accidents. Therefore, precise management and guaranteed safety are essential.<br/>ORBRO offers various functions to build safe and efficient medical facilities.',

    data: [
      { icon: IconBinocularsGray, title: 'PTZ Vehicle Tracking', desc: 'High-zoom PTZ for wide lots.' },
      { icon: IconChartBarGray, title: 'AI Object Detection', desc: 'Auto detect cars, people.' },
      { icon: IconClockGray, title: 'Night/Low Light Ready', desc: 'Stable detect in darkness.' },
      { icon: IconChartBarGray, title: 'Bad Weather Ready', desc: 'Snow/rain tracking stable.' },
      { icon: IconChartRadius, title: 'Indoor Flow Analysis', desc: 'Fish-eye shows crowd flow.' },
      { icon: IconRoute, title: 'Traffic Entry/Exit', desc: 'Log time data for traffic.' },
      { icon: IconZoneGray, title: 'ROI Video Filter', desc: 'Keep needed areas only.' },
      { icon: IconWarning, title: 'Wrong Way Alert', desc: 'Reverse car triggers alert.' },
      { icon: IconWarning, title: 'Long Stay Ped Alert', desc: 'Long stop triggers warning.' },
      { icon: IconDashboard, title: 'AI Dashboard View', desc: 'Live flow & alerts in one.' },
    ],
  },

  system: {
    title: 'System Configuration',
    img: rtlsSystem,
  },

  productTitle: 'Product Configuration',
  product: [{ tag: 'assetPackage' }, { tag: 'workerPackage' }],

  contents: [{ data: 'medicalPatient' }, { data: 'officeAsset' }, { data: 'blogMedical' }, { data: 'blogRTLS' }],
};
